import type { EnrichedFixture } from "../../shared/types";
import { flagFor } from "../../shared/flags";
import { formatLocalMatchTime, getLocalDateKey } from "../lib/dates";
import { isKickedOffAwaitingData } from "../lib/fixtures";
import OwnerTag from "./OwnerTag";

function liveBadge(fixture: EnrichedFixture): string {
  const raw = fixture.rawStatus;
  const minutes = fixture.elapsed ?? 0;
  switch (raw) {
    case "PAUSED":
      return "HALF-TIME";
    case "SUSPENDED":
      return "SUSPENDED";
    case "IN_PLAY":
    default:
      return `LIVE${minutes ? ` · ${minutes}'` : ""}`;
  }
}

function stageLabel(fixture: EnrichedFixture): string {
  if (fixture.stageType === "group") {
    return fixture.group
      ? `Group stage · ${formatGroup(fixture.group)}`
      : "Group stage";
  }
  return fixture.stage;
}

function formatGroup(group: string) {
  if (group.startsWith("GROUP_")) {
    return `Group ${group.slice(6)}`;
  }
  return group;
}

function isToday(dateUtc: string): boolean {
  return getLocalDateKey(dateUtc) === getLocalDateKey(new Date().toISOString());
}

function formatSignedNum(n: number): string {
  if (n > 0) return `+${n}`;
  return String(n);
}

type SideProps = {
  align: "left" | "right";
  owner: string | null;
  team: string;
  stat?: string;
  dimmed?: boolean;
};

function Side({ align, owner, team, stat, dimmed }: SideProps) {
  return (
    <div
      className={
        "h2h-side h2h-side--" + align + (dimmed ? " h2h-side--dim" : "")
      }
    >
      <div className="h2h-owner-name">
        {owner ? (
          <OwnerTag owner={owner} size="lg" />
        ) : (
          <span className="h2h-owner-name--tbd">—</span>
        )}
      </div>
      <div className="h2h-team-line">
        <span className="h2h-flag" aria-hidden="true">
          {flagFor(team)}
        </span>
        <span className="h2h-team-name">{team}</span>
      </div>
      {stat ? <div className="h2h-side-stat">{stat}</div> : null}
    </div>
  );
}

export default function HeadToHeadCard({
  fixture,
}: {
  fixture: EnrichedFixture;
}) {
  if (fixture.isPlaceholder) {
    return (
      <article
        className="h2h-card h2h-card--placeholder"
        aria-label="Placeholder fixture"
      >
        <div className="h2h-matchup">
          <div className="h2h-side h2h-side--left">
            <div className="h2h-owner-name h2h-owner-name--tbd">TBD</div>
            <div className="h2h-team-line">
              <span className="h2h-flag" aria-hidden="true">
                🏳️
              </span>
              <span className="h2h-team-name">TBD</span>
            </div>
          </div>
          <div className="h2h-mid h2h-mid--v">v</div>
          <div className="h2h-side h2h-side--right">
            <div className="h2h-owner-name h2h-owner-name--tbd">TBD</div>
            <div className="h2h-team-line">
              <span className="h2h-team-name">TBD</span>
              <span className="h2h-flag" aria-hidden="true">
                🏳️
              </span>
            </div>
          </div>
        </div>
        <div className="h2h-meta">
          {fixture.stage} · {formatLocalMatchTime(fixture.dateUtc)}
        </div>
      </article>
    );
  }

  if (fixture.status === "live") {
    return (
      <article className="h2h-card h2h-card--live" aria-label="Live fixture">
        <div className="h2h-status-row">
          <span className="status-badge live">{liveBadge(fixture)}</span>
        </div>
        <div className="h2h-matchup">
          <Side
            align="left"
            owner={fixture.homeOwner}
            team={fixture.homeTeam}
          />
          <div className="h2h-mid h2h-mid--score">
            <span className="h2h-score-num">{fixture.homeGoals ?? 0}</span>
            <span className="h2h-score-dash">–</span>
            <span className="h2h-score-num">{fixture.awayGoals ?? 0}</span>
          </div>
          <Side
            align="right"
            owner={fixture.awayOwner}
            team={fixture.awayTeam}
          />
        </div>
        <div className="h2h-meta">{stageLabel(fixture)}</div>
      </article>
    );
  }

  if (fixture.status === "finished") {
    const draw =
      fixture.homeGoals != null &&
      fixture.awayGoals != null &&
      fixture.homeGoals === fixture.awayGoals;

    // Primary side wins (or, for draws, home). Score and grid order follow
    // the headline so a reader can scan one column top-to-bottom.
    const winnerIsAway =
      !draw && (fixture.awayGoals ?? 0) > (fixture.homeGoals ?? 0);
    const primary = winnerIsAway ? "away" : "home";

    const pTeam = primary === "home" ? fixture.homeTeam : fixture.awayTeam;
    const sTeam = primary === "home" ? fixture.awayTeam : fixture.homeTeam;
    const pGoals = primary === "home" ? fixture.homeGoals : fixture.awayGoals;
    const sGoals = primary === "home" ? fixture.awayGoals : fixture.homeGoals;
    const pOwner = primary === "home" ? fixture.homeOwner : fixture.awayOwner;
    const sOwner = primary === "home" ? fixture.awayOwner : fixture.homeOwner;

    const sameOwner = pOwner && sOwner && pOwner === sOwner;

    const headline = draw
      ? sameOwner
        ? `${pOwner} drew with themselves`
        : `${pOwner ?? "?"} drew with ${sOwner ?? "?"}`
      : sameOwner
        ? `${pOwner} v ${sOwner}`
        : `${pOwner ?? "?"} beat ${sOwner ?? "?"}`;

    const pGD = (pGoals ?? 0) - (sGoals ?? 0);
    const sGD = -pGD;
    const isPenalties = fixture.rawStatus === "PEN";

    return (
      <article
        className="h2h-card h2h-card--finished"
        aria-label="Finished fixture"
      >
        <div className="h2h-headline">{headline}</div>
        <div className="h2h-matchup">
          <Side
            align="left"
            owner={pOwner}
            team={pTeam ?? "TBD"}
            stat={`GD ${formatSignedNum(pGD)} · GF ${pGoals ?? 0}`}
          />
          <div className="h2h-mid h2h-mid--score">
            <span className="h2h-score-num h2h-score-num--win">{pGoals}</span>
            <span className="h2h-score-dash">–</span>
            <span className="h2h-score-num">{sGoals}</span>
          </div>
          <Side
            align="right"
            owner={sOwner}
            team={sTeam ?? "TBD"}
            stat={`GD ${formatSignedNum(sGD)} · GF ${sGoals ?? 0}`}
            dimmed={!draw && !sameOwner}
          />
        </div>
        <div className="h2h-meta">
          <span className="status-badge finished">Finished</span>
          <span> · {stageLabel(fixture)}</span>
          {isPenalties ? <span> · on penalties</span> : null}
        </div>
      </article>
    );
  }

  const kickedOff = isKickedOffAwaitingData(fixture);

  return (
    <article
      className="h2h-card h2h-card--scheduled"
      aria-label={
        kickedOff ? "Fixture kicked off, awaiting score data" : "Scheduled fixture"
      }
    >
      {kickedOff ? (
        <div className="h2h-status-row">
          <span className="status-badge kicked-off">Kicked off</span>
        </div>
      ) : null}
      <div className="h2h-matchup">
        <Side
          align="left"
          owner={fixture.homeOwner}
          team={fixture.homeTeam}
        />
        <div className="h2h-mid h2h-mid--v">v</div>
        <Side
          align="right"
          owner={fixture.awayOwner}
          team={fixture.awayTeam}
        />
      </div>
      <div className="h2h-meta">
        {kickedOff
          ? `Awaiting score data · kicked off ${formatLocalMatchTime(fixture.dateUtc)}`
          : isToday(fixture.dateUtc)
            ? `Today · ${formatLocalMatchTime(fixture.dateUtc)}`
            : formatLocalMatchTime(fixture.dateUtc)}
        {" · "}
        {stageLabel(fixture)}
      </div>
    </article>
  );
}
